'use client'

import { useState } from 'react'
import Image from 'next/image'
import { faCog, faSignOut, faStars, faTable, faUsers } from '@fortawesome/pro-light-svg-icons'
import { Menu, MenuHandler, MenuItem, MenuLink, MenuList } from '@ui/menu'
import { cn } from '@utils/cn'
import { logout } from '@view/auth/api'
import { ChevronDownIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'

import { useAppStore } from '@/entities/app-store'
import { productAnalytics } from '@/shared/analytics/product-analytics'
import { ACCOUNT_SUBSCRIPTION_PATH } from './account-user-menu-links'

const AccountUserMenu = () => {
  const appStore = useAppStore()

  const [open, setOpen] = useState<boolean>(false)
  const setOpenHandler = () => setOpen(!open)

  const user = appStore.appState.user
  const avatar = user?.avatar || ''
  const project = appStore.project
  const canManageUsers = project?.role === 'ROLE_PROJECT_OWNER' || project?.role === 'ROLE_PROJECT_ADMIN'

  return user?.id ? (
    <div>
      <Menu offset={5} placement="bottom-end" open={open} handler={setOpenHandler}>
        <MenuHandler
          className={cn(
            open
              ? 'bg-blue-100 text-blue-500 rounded-md pt-[3px] pb-[5px] pr-[0px]'
              : 'rounded-md pt-[3px] pb-[5px] pr-[0px] hover:opacity-80 transition-opacity duration-220 ease-out'
          )}
        >
          <div>
            <Image className="rounded-full cursor-pointer" src={avatar} width={40} height={40} alt="Пользователь" />
            <div className="ml-1 pr-1 cursor-pointer">
              <div className="font-semibold max-w-[220px] truncate overflow-hidden text-[#707070] text-[12px] text-[normal]">
                {user?.fullName}
              </div>
              <div className="flex">
                <span className="max-w-[220px] text-[#191919] font-medium text-[13px] truncate overflow-hidden">
                  {user?.email}
                </span>
                <ChevronDownIcon
                  className={`${open ? 'text-blue-500' : 'text-slate-500'} ml-0 h-5 w-5 transition duration-150 ease-in-out group-hover:text-blue-500`}
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </MenuHandler>

        <MenuList className="min-w-[210px] w-auto">
          <MenuItem onClick={() => setOpen(false)}>
            <MenuLink href={{ pathname: '/my' }} icon={faTable}>
              РНП
            </MenuLink>
          </MenuItem>

          {canManageUsers && (
            <MenuItem onClick={() => setOpen(false)}>
              <MenuLink href={{ pathname: '/my/project/settings/users' }} icon={faUsers}>
                Пользователи
              </MenuLink>
            </MenuItem>
          )}

          <MenuItem onClick={() => setOpen(false)}>
            <MenuLink href={{ pathname: ACCOUNT_SUBSCRIPTION_PATH }} icon={faStars}>
              Подписка
            </MenuLink>
          </MenuItem>

          <MenuItem onClick={() => setOpen(false)}>
            <MenuLink href={{ pathname: '/my/project/settings' }} icon={faCog}>
              Настройки
            </MenuLink>
          </MenuItem>

          <MenuItem className="border-t">
            <MenuLink
              href="#"
              onClick={(e) => {
                e.preventDefault()
                logout().then((data) => {
                  if (data.data['switch-to-user'] === 'deleted') {
                    window.location.href = '/my'
                  } else {
                    productAnalytics.reset()
                    window.location.href = '/auth/login'
                  }
                })
              }}
              icon={faSignOut}
            >
              Выйти
            </MenuLink>
          </MenuItem>
        </MenuList>
      </Menu>
    </div>
  ) : (
    <div role="status" className="flex rounded-full text-sm ml-2 p-2 max-w-sm bg-gray-300 animate-pulse">
      <div className="w-5 h-5"></div>
    </div>
  )
}

export default observer(AccountUserMenu)
